import { Check, CheckCircle2, Quote, TriangleAlert, Undo2, Wand2, X } from "lucide-react";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { adjustScene, getAdjustContext, type AdjustSceneAction, type BacklogEntry } from "../api";

/**
 * SceneDetailModal — everything the operator needs to decide whether a
 * scanned scene is worth render credits, in one scrollable popup:
 * summary (the arc), the payoff line + takeaway, the ending-judge note,
 * why-it-works, speaker/topic/source meta, and the full transcript excerpt.
 *
 * Plus, for un-rendered scan/locate candidates: **Adjust scene** — a surgical
 * pre-render boundary editor. Two mechanisms, mirroring how the operator
 * thinks about a scene:
 *   - ±seconds nudges on the start/end (server clamps to the hard clip cap);
 *   - editable "Opens on" / "Cuts after" lines that re-anchor a boundary to
 *     the cached transcript (fuzzy, ±120s around the current window). The
 *     server never guesses: a miss comes back as an inline message.
 * Adjustments persist on the candidate (from/to/startPhrase/endPhrase +
 * noSnap), so Render picks them up through the unchanged job path, and the
 * scanner's original pick stays one "Reset" away.
 *
 * Opened by clicking a SceneRow's content area. Shell follows the app's
 * modal pattern (BetaPostCard): fixed overlay, 3-row grid (pinned header /
 * scrollable body / pinned footer), body-scroll lock + Escape + backdrop
 * click to close. Footer actions are passed in by SceneRow so render state
 * lives in one place.
 */
export function SceneDetailModal({
  entry,
  isMobile,
  onClose,
  footer,
  onEntryUpdated,
}: {
  entry: BacklogEntry;
  isMobile: boolean;
  onClose: () => void;
  /** Action buttons (Render / Open render), owned by SceneRow. */
  footer?: React.ReactNode;
  /** Persist-side channel for boundary adjustments: the modal PATCHes via
   *  POST /api/backlog/:id/adjust and bubbles the updated entry up so the
   *  parent list (and the next Render) sees the adjusted window. Absent →
   *  the Adjust section is hidden (read-only contexts). */
  onEntryUpdated?: (updated: BacklogEntry) => void;
}): React.JSX.Element {
  // Escape closes; body scroll locks while open (app-wide modal idiom).
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  const spanLabel = `${entry.from ?? "?"} → ${entry.to ?? "auto"}`;
  const durationSec = spanSeconds(entry.from, entry.to);

  // ── Adjust scene (pre-render boundary editor) ──────────────────────────────
  // Only for un-rendered scan/locate candidates with a persistence channel —
  // manual rows are edited in the New Job form, rendered rows via Cut & Extend.
  const adjustable =
    !!onEntryUpdated && !entry.renderedJobId && (entry.origin === "scan" || entry.origin === "locate");

  const [ctx, setCtx] = useState<{ transcriptAvailable: boolean; maxClipSec: number } | null>(null);
  const [busy, setBusy] = useState<
    null | "nudge-start" | "nudge-end" | "anchor-start" | "anchor-end" | "reset"
  >(null);
  const [notice, setNotice] = useState<{ tone: "ok" | "warn" | "err"; text: string } | null>(null);
  // Editable "Opens on" / "Cuts after" drafts, re-seeded whenever the stored
  // phrase changes out from under them (a nudge clears the matching phrase).
  const [openDraft, setOpenDraft] = useState(entry.startPhrase ?? "");
  const [closeDraft, setCloseDraft] = useState(entry.endPhrase ?? "");
  useEffect(() => { setOpenDraft(entry.startPhrase ?? ""); }, [entry.startPhrase]);
  useEffect(() => { setCloseDraft(entry.endPhrase ?? ""); }, [entry.endPhrase]);

  // Pre-flight: can we phrase-anchor (cached transcript present), and what is
  // the server's hard clip cap (single source of truth — never hardcoded).
  useEffect(() => {
    if (!adjustable) return;
    let alive = true;
    getAdjustContext(entry.id)
      .then((c) => { if (alive) setCtx(c); })
      // Degrade honestly: phrase fields disable, nudges still work (the
      // server clamps to the real cap regardless of what we display).
      .catch(() => { if (alive) setCtx({ transcriptAvailable: false, maxClipSec: 180 }); });
    return () => { alive = false; };
  }, [adjustable, entry.id]);

  const runAdjust = async (
    key: NonNullable<typeof busy>,
    action: AdjustSceneAction,
  ): Promise<void> => {
    if (busy) return;
    setBusy(key);
    setNotice(null);
    try {
      const r = await adjustScene(entry.id, action);
      if (!r.found) {
        // Domain outcome (phrase not in transcript / nonsense window) — the
        // server mutated NOTHING and never guesses. Tell the operator plainly.
        setNotice({ tone: "warn", text: r.message ?? "Couldn't apply that adjustment." });
        return;
      }
      onEntryUpdated?.(r.entry);
      const cap = ctx?.maxClipSec;
      if (action.action === "reset") {
        setNotice({ tone: "ok", text: "Back to the scanner's pick." });
      } else if (action.action === "anchor" && r.match) {
        const lead = action.edge === "start" ? "Now opens on" : "Now cuts after";
        const at = action.edge === "start" ? r.entry.from : r.entry.to;
        const tail = r.overCap
          ? ` — over the ${cap ?? "?"}s ceiling: the render trims and may lose this ending. Move the start later to keep it.`
          : "";
        setNotice({
          tone: r.overCap ? "warn" : "ok",
          text: `${lead} “${r.match.matchedText}”${at ? ` — ${action.edge} moved to ${at}` : ""}${r.match.exact ? "" : " (closest match)"}${tail}`,
        });
      } else if (r.clamped) {
        setNotice({ tone: "warn", text: `Clamped — clips are capped at ${cap ?? "?"}s.` });
      }
    } catch (err) {
      setNotice({ tone: "err", text: err instanceof Error ? err.message : "Adjustment failed." });
    } finally {
      setBusy(null);
    }
  };

  const verdict = entry.endingVerdict;
  const verdictTone =
    verdict?.verdict === "warn"
      ? { color: "var(--ql-yellow)", bg: "rgba(255,213,0,.08)", border: "rgba(255,213,0,.25)" }
      : verdict?.verdict === "extended"
      ? { color: "#60a5fa", bg: "rgba(96,165,250,.08)", border: "rgba(96,165,250,.30)" }
      : { color: "var(--ql-success)", bg: "rgba(74,222,128,.08)", border: "rgba(74,222,128,.25)" };
  const verdictLabel =
    verdict?.verdict === "warn"
      ? "Ending check — weak ending"
      : verdict?.verdict === "extended"
      ? "Ending check — end moved to the payoff"
      : "Ending check — resolves";
  const VerdictIcon =
    verdict?.verdict === "warn" ? TriangleAlert : verdict?.verdict === "extended" ? Wand2 : CheckCircle2;

  // PORTAL to <body>: the modal is mounted inside a SceneRow whose root div
  // dims itself for rendered scenes (opacity .7). An ancestor with opacity < 1
  // creates a new stacking context, which traps the overlay's z-index (later
  // rows paint OVER the popup) and dims the whole popup to 70% — the "broken
  // see-through modal on rendered scenes" bug. Escaping to <body> makes the
  // popup immune to ANY row styling, present or future.
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Scene details"
      // Close on CLICK (not mousedown): removing the modal mid-mousedown lets
      // iOS Safari/touch synthesize a click on the SceneRow content div behind
      // the backdrop — which re-opens the modal. With click, the modal is
      // still mounted for the full mousedown→mouseup→click sequence, so the
      // dismiss can never leak through to the row.
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(6,8,14,.66)",
        backdropFilter: "blur(8px)",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: isMobile ? 12 : 24,
        animation: "ql-fade-in .15s var(--ql-ease)",
      }}
    >
      <div
        style={{
          width: isMobile ? "calc(100vw - 24px)" : "min(760px, calc(100vw - 48px))",
          maxHeight: "92vh",
          background: "rgba(16,18,27,.96)",
          border: "1px solid rgba(255,255,255,.10)",
          borderRadius: 18,
          boxShadow: "0 24px 60px rgba(0,0,0,.45)",
          display: "grid",
          gridTemplateRows: "auto 1fr auto",
          overflow: "hidden",
          animation: "ql-pop-in .18s var(--ql-ease)",
        }}
      >
        {/* Header — pinned */}
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 12,
            padding: isMobile ? "12px 14px" : "16px 22px",
            borderBottom: "1px solid rgba(255,255,255,.08)",
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: isMobile ? 15 : 17, fontWeight: 700, color: "var(--ql-fg)", lineHeight: 1.35 }}>
              {entry.hookEn ?? entry.label ?? "(no hook)"}
            </div>
            <div
              style={{
                marginTop: 6,
                display: "flex",
                alignItems: "center",
                gap: 10,
                flexWrap: "wrap",
                fontSize: 11,
                color: "var(--ql-fg-muted)",
                fontFamily: "var(--ql-font-mono)",
              }}
            >
              <span>{spanLabel}</span>
              {durationSec !== null && <span>~{durationSec}s</span>}
              {entry.confidence !== undefined && entry.origin !== "locate" && (
                <span>{Math.round(entry.confidence * 100)}% match</span>
              )}
              {entry.origin === "locate" && <span>from your anchor</span>}
            </div>
          </div>
          <button
            onClick={onClose}
            title="Close"
            aria-label="Close scene details"
            autoFocus
            style={{
              width: 32,
              height: 32,
              flexShrink: 0,
              borderRadius: 10,
              border: "1px solid rgba(255,255,255,.10)",
              background: "rgba(255,255,255,.03)",
              color: "var(--ql-fg-muted)",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <X size={15} strokeWidth={2} />
          </button>
        </div>

        {/* Body — the only scrolling region */}
        <div style={{ overflowY: "auto", padding: isMobile ? 14 : 22, display: "flex", flexDirection: "column", gap: 16 }}>
          {/* What happens — the arc summary. */}
          {entry.summary && (
            <section>
              <SectionLabel>What happens</SectionLabel>
              <div style={{ fontSize: 13.5, lineHeight: 1.65, color: "var(--ql-fg)" }}>{entry.summary}</div>
            </section>
          )}

          {/* How it ends — the payoff line + the takeaway. */}
          {(entry.payoffLine || entry.resolutionInsight) && (
            <section>
              <SectionLabel>How it ends</SectionLabel>
              {entry.payoffLine && (
                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    padding: "10px 14px",
                    borderRadius: 12,
                    background: "rgba(255,213,0,.05)",
                    border: "1px solid rgba(255,213,0,.15)",
                    fontSize: 13.5,
                    lineHeight: 1.6,
                    color: "var(--ql-fg)",
                    fontStyle: "italic",
                  }}
                >
                  <Quote size={14} strokeWidth={2} style={{ flexShrink: 0, marginTop: 3, opacity: 0.6 }} />
                  <span>“{entry.payoffLine}”</span>
                </div>
              )}
              {entry.resolutionInsight && (
                <div style={{ marginTop: 8, fontSize: 12.5, lineHeight: 1.6, color: "var(--ql-fg-muted)" }}>
                  <span style={{ fontWeight: 700, color: "var(--ql-fg-muted)" }}>Takeaway: </span>
                  {entry.resolutionInsight}
                </div>
              )}
            </section>
          )}

          {/* Ending-judge note — the "viewer test" verdict. */}
          {verdict && (
            <section>
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  alignItems: "flex-start",
                  padding: "10px 14px",
                  borderRadius: 12,
                  background: verdictTone.bg,
                  border: `1px solid ${verdictTone.border}`,
                }}
              >
                <VerdictIcon size={15} strokeWidth={2} style={{ flexShrink: 0, marginTop: 2, color: verdictTone.color }} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: verdictTone.color }}>
                    {verdictLabel}
                    <span style={{ fontFamily: "var(--ql-font-mono)", fontWeight: 600, opacity: 0.85 }}>
                      {" "}
                      · {Math.round(verdict.score * 100)}%
                    </span>
                  </div>
                  <div style={{ marginTop: 3, fontSize: 12, lineHeight: 1.55, color: "var(--ql-fg-muted)" }}>
                    {verdict.reason}
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* Adjust scene — surgical pre-render boundary editor (candidates only). */}
          {adjustable && (
            <section>
              <SectionLabel>Adjust scene</SectionLabel>

              {/* Original vs adjusted window + reset. */}
              {entry.adjusted && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    flexWrap: "wrap",
                    marginBottom: 10,
                    fontSize: 12,
                    fontFamily: "var(--ql-font-mono)",
                  }}
                >
                  <span style={{ color: "var(--ql-fg-subtle)", textDecoration: "line-through" }}>
                    {entry.adjusted.original.from ?? "?"} → {entry.adjusted.original.to ?? "auto"}
                  </span>
                  <span style={{ color: "var(--ql-fg-subtle)" }}>→</span>
                  <span style={{ color: "var(--ql-yellow)", fontWeight: 700 }}>
                    {entry.from ?? "?"} → {entry.to ?? "auto"}
                    {durationSec !== null ? ` · ~${durationSec}s` : ""}
                  </span>
                  <button
                    onClick={() => void runAdjust("reset", { action: "reset" })}
                    disabled={busy !== null}
                    title="Undo every adjustment — back to the scanner's original window and lines"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 5,
                      padding: "4px 10px",
                      borderRadius: 8,
                      fontSize: 11,
                      fontWeight: 600,
                      fontFamily: "inherit",
                      background: "rgba(255,255,255,.03)",
                      border: "1px solid rgba(255,255,255,.12)",
                      color: "var(--ql-fg-muted)",
                      cursor: busy ? "wait" : "pointer",
                      opacity: busy ? 0.6 : 1,
                    }}
                  >
                    <Undo2 size={12} strokeWidth={2} /> Reset to scanner's pick
                  </button>
                </div>
              )}

              {/* ±seconds nudges, one row per edge. */}
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {(["start", "end"] as const).map((edge) => (
                  <div
                    key={edge}
                    style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}
                  >
                    <span
                      style={{
                        width: 38,
                        fontSize: 11,
                        fontWeight: 700,
                        color: "var(--ql-fg-muted)",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                      }}
                    >
                      {edge}
                    </span>
                    <span
                      style={{
                        fontSize: 12,
                        fontFamily: "var(--ql-font-mono)",
                        color: "var(--ql-fg)",
                        minWidth: 44,
                      }}
                    >
                      {edge === "start" ? entry.from ?? "?" : entry.to ?? "auto"}
                    </span>
                    {[-5, -1, 1, 5].map((d) => (
                      <NudgePill
                        key={d}
                        deltaSec={d}
                        disabled={busy !== null}
                        onClick={() =>
                          void runAdjust(edge === "start" ? "nudge-start" : "nudge-end", {
                            action: "nudge",
                            edge,
                            deltaSec: d,
                          })
                        }
                        title={`${edge === "start" ? "Start" : "End"} ${Math.abs(d)}s ${d < 0 ? "earlier" : "later"}`}
                      />
                    ))}
                  </div>
                ))}
              </div>

              {/* Live duration vs the hard cap. */}
              {durationSec !== null && ctx && (
                <div
                  style={{
                    marginTop: 8,
                    fontSize: 11,
                    fontFamily: "var(--ql-font-mono)",
                    color: durationSec >= ctx.maxClipSec ? "var(--ql-yellow)" : "var(--ql-fg-subtle)",
                  }}
                >
                  ~{durationSec}s (ceiling {ctx.maxClipSec}s)
                  {durationSec > ctx.maxClipSec
                    ? " — over the ceiling; the render trims and may not end where you chose."
                    : durationSec === ctx.maxClipSec
                    ? " — at the ceiling."
                    : ""}
                </div>
              )}

              {/* Semantic re-anchor: editable Opens on / Cuts after. */}
              <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
                {ctx && !ctx.transcriptAvailable && (
                  <div style={{ fontSize: 11.5, lineHeight: 1.5, color: "var(--ql-fg-muted)" }}>
                    No cached transcript for this video — editing the opening/closing lines is
                    unavailable. The second-nudges above still work.
                  </div>
                )}
                <PhraseEditor
                  label="Opens on"
                  placeholder="the exact words the scene should open on…"
                  value={openDraft}
                  onChange={setOpenDraft}
                  disabled={!ctx?.transcriptAvailable || busy !== null}
                  applying={busy === "anchor-start"}
                  onApply={() =>
                    void runAdjust("anchor-start", { action: "anchor", edge: "start", phrase: openDraft })
                  }
                />
                <PhraseEditor
                  label="Cuts after"
                  placeholder="the exact words the scene should end on…"
                  value={closeDraft}
                  onChange={setCloseDraft}
                  disabled={!ctx?.transcriptAvailable || busy !== null}
                  applying={busy === "anchor-end"}
                  onApply={() =>
                    void runAdjust("anchor-end", { action: "anchor", edge: "end", phrase: closeDraft })
                  }
                />
              </div>

              {/* Outcome line: snap confirmations, not-found, clamps, errors. */}
              {notice && (
                // Loud on purpose: the operator missed the old body-text
                // version entirely and thought Snap did nothing.
                <div
                  style={{
                    marginTop: 10,
                    padding: "10px 12px",
                    borderRadius: 8,
                    fontSize: 12.5,
                    fontWeight: 600,
                    lineHeight: 1.55,
                    display: "flex",
                    gap: 8,
                    alignItems: "flex-start",
                    color:
                      notice.tone === "ok"
                        ? "var(--ql-success)"
                        : notice.tone === "warn"
                        ? "var(--ql-yellow)"
                        : "var(--ql-danger)",
                    background:
                      notice.tone === "ok"
                        ? "rgba(74,222,128,.10)"
                        : notice.tone === "warn"
                        ? "rgba(255,213,0,.10)"
                        : "rgba(248,113,113,.10)",
                    border: `1px solid ${
                      notice.tone === "ok"
                        ? "rgba(74,222,128,.35)"
                        : notice.tone === "warn"
                        ? "rgba(255,213,0,.35)"
                        : "rgba(248,113,113,.35)"
                    }`,
                  }}
                >
                  <span aria-hidden>{notice.tone === "ok" ? "✓" : "⚠"}</span>
                  <span>{notice.text}</span>
                </div>
              )}
            </section>
          )}

          {/* Why it works — same note the row shows, kept for one-stop reading. */}
          {entry.whyItWorks && (
            <section>
              <SectionLabel>Why it works</SectionLabel>
              <div style={{ fontSize: 12.5, lineHeight: 1.6, color: "var(--ql-fg-muted)" }}>{entry.whyItWorks}</div>
            </section>
          )}

          {/* Meta grid. */}
          <section>
            <SectionLabel>Details</SectionLabel>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: "6px 18px", fontSize: 12.5 }}>
              {entry.host && <MetaItem label="Speaker" value={entry.host} />}
              {entry.guests && entry.guests.length > 0 && <MetaItem label="Guests" value={entry.guests.join(", ")} />}
              {entry.topic && <MetaItem label="Topic" value={entry.topic} />}
              {entry.sourceTitle && <MetaItem label="Source" value={entry.sourceTitle} />}
              {/* When the Adjust section is shown, its editable fields ARE the
                  Opens-on / Cuts-after display — skip the static duplicates. */}
              {!adjustable && entry.startPhrase && <MetaItem label="Opens on" value={`“${entry.startPhrase}”`} />}
              {!adjustable && entry.endPhrase && <MetaItem label="Cuts after" value={`“${entry.endPhrase}”`} />}
            </div>
          </section>

          {/* Full transcript excerpt — what the viewer will hear, word for word. */}
          {entry.sceneTranscript && (
            <section>
              <SectionLabel>Transcript</SectionLabel>
              <div
                dir="auto"
                style={{
                  padding: "12px 14px",
                  borderRadius: 12,
                  background: "rgba(255,255,255,.03)",
                  border: "1px solid rgba(255,255,255,.08)",
                  fontSize: 13,
                  lineHeight: 1.75,
                  color: "var(--ql-fg)",
                  whiteSpace: "pre-wrap",
                  overflowWrap: "anywhere",
                }}
              >
                {entry.sceneTranscript}
              </div>
            </section>
          )}
        </div>

        {/* Footer — pinned actions (owned by SceneRow). */}
        {footer && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 8,
              padding: isMobile ? "10px 14px" : "12px 22px",
              borderTop: "1px solid rgba(255,255,255,.08)",
            }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

function SectionLabel({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <div
      style={{
        fontSize: 10.5,
        fontWeight: 700,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: "var(--ql-fg-subtle)",
        marginBottom: 6,
      }}
    >
      {children}
    </div>
  );
}

function MetaItem({ label, value }: { label: string; value: string }): React.JSX.Element {
  return (
    <div style={{ minWidth: 0 }}>
      <span style={{ color: "var(--ql-fg-subtle)" }}>{label}: </span>
      <span style={{ color: "var(--ql-fg-muted)", overflowWrap: "anywhere" }}>{value}</span>
    </div>
  );
}

/** One ±N-seconds nudge pill (Adjust scene section). */
function NudgePill({
  deltaSec,
  disabled,
  onClick,
  title,
}: {
  deltaSec: number;
  disabled: boolean;
  onClick: () => void;
  title: string;
}): React.JSX.Element {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={{
        padding: "4px 9px",
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 700,
        fontFamily: "var(--ql-font-mono)",
        background: "rgba(255,255,255,.04)",
        border: "1px solid rgba(255,255,255,.12)",
        color: "var(--ql-fg-muted)",
        cursor: disabled ? "wait" : "pointer",
        opacity: disabled ? 0.55 : 1,
      }}
    >
      {deltaSec > 0 ? `+${deltaSec}s` : `${deltaSec}s`}
    </button>
  );
}

/** Editable "Opens on" / "Cuts after" line with an Apply (snap) button. */
function PhraseEditor({
  label,
  placeholder,
  value,
  onChange,
  disabled,
  applying,
  onApply,
}: {
  label: string;
  placeholder: string;
  value: string;
  onChange: (v: string) => void;
  disabled: boolean;
  applying: boolean;
  onApply: () => void;
}): React.JSX.Element {
  const canApply = !disabled && value.trim().length > 0;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <span
        style={{
          width: 64,
          flexShrink: 0,
          fontSize: 11,
          fontWeight: 600,
          color: "var(--ql-fg-subtle)",
        }}
      >
        {label}
      </span>
      <input
        type="text"
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && canApply) onApply();
        }}
        aria-label={`${label} — edit the line, then snap the boundary to it`}
        style={{
          flex: 1,
          minWidth: 0,
          padding: "7px 10px",
          borderRadius: 10,
          fontSize: 12.5,
          fontFamily: "inherit",
          background: "rgba(255,255,255,.04)",
          border: "1px solid rgba(255,255,255,.12)",
          color: "var(--ql-fg)",
          opacity: disabled ? 0.55 : 1,
        }}
      />
      <button
        onClick={onApply}
        disabled={!canApply}
        title={
          disabled
            ? "Unavailable — no cached transcript for this video"
            : "Find this line in the transcript and snap the boundary to it"
        }
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 5,
          padding: "6px 11px",
          borderRadius: 10,
          fontSize: 11.5,
          fontWeight: 700,
          fontFamily: "inherit",
          flexShrink: 0,
          background: canApply ? "rgba(255,213,0,.12)" : "rgba(255,255,255,.03)",
          border: canApply ? "1px solid rgba(255,213,0,.35)" : "1px solid rgba(255,255,255,.10)",
          color: canApply ? "var(--ql-yellow)" : "var(--ql-fg-subtle)",
          cursor: canApply ? "pointer" : "not-allowed",
        }}
      >
        {applying ? (
          <span
            style={{
              display: "inline-block",
              animation: "ql-spin 1s linear infinite",
              width: 11,
              height: 11,
              border: "2px solid currentColor",
              borderTopColor: "transparent",
              borderRadius: "50%",
            }}
          />
        ) : (
          <Check size={12} strokeWidth={2.5} />
        )}
        Snap
      </button>
    </div>
  );
}

/** Parse "m:ss" → seconds; null on anything unparseable. */
function parseTimestamp(ts?: string): number | null {
  if (!ts) return null;
  const m = /^(\d+):(\d{1,2})$/.exec(ts.trim());
  if (!m) return null;
  return Number(m[1]) * 60 + Number(m[2]);
}

/** Whole-second span between the entry's from/to labels; null when unknown. */
function spanSeconds(from?: string, to?: string): number | null {
  const a = parseTimestamp(from);
  const b = parseTimestamp(to);
  if (a === null || b === null || b <= a) return null;
  return Math.round(b - a);
}
